<app-billytics-public-container-component>
  <div class="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
    <!-- Header -->
    <div class="max-w-4xl mx-auto text-center mb-10">
      <h1 class="text-3xl font-bold">Privacy Policy — Billytics</h1>
      <p class="mt-2 text-sm">Last updated: September 2025</p>
    </div>

    <mat-card class="max-w-4xl mx-auto">
      <mat-card-content>
        <!-- Section 1 -->
        <section>
          <h2 class="text-xl font-semibold mb-2">1. Scope & Acceptance</h2>
          <p>
            Billytics (“we,” “our,” or “us”) is committed to protecting your privacy. This Privacy
            Policy explains how we collect, use, store, and share information when you use the
            Billytics AI-based application (“Service”). By accessing or using the Service, you agree
            to this Privacy Policy.
          </p>
        </section>

        <!-- Section 2 -->
        <section>
          <h2 class="text-xl font-semibold mb-2">2. Information We Collect</h2>
          <h3 class="text-lg font-medium mt-2">a. Account Information</h3>
          <p>Name, email address, and contact details when you create an account.</p>

          <h3 class="text-lg font-medium mt-2">b. Fetched Data from Emails</h3>
          <ul class="list-disc pl-6 space-y-1">
            <li>Secure token-based connections to your email—no storing of email credentials.</li>
            <li>
              Option to manually fetch data or enable automatic monitoring of bills, receipts, and
              invoices.
            </li>
            <li>You can toggle email monitoring on/off in the app.</li>
          </ul>

          <h3 class="text-lg font-medium mt-2">c. Additional Stored Information</h3>
          <ul class="list-disc pl-6 space-y-1">
            <li>Device info (e.g., device type, OS), country, IP address</li>
            <li>Page views, clicks, session stats, UUID, and your email address</li>
            <li>Support communications, feedback, and user counts/data usage stats</li>
          </ul>
        </section>

        <!-- Section 3 -->
        <section>
          <h2 class="text-xl font-semibold mb-2">3. How We Use Your Information</h2>
          <ul class="list-disc pl-6 space-y-1">
            <li>
              Provide and improve the Service—analyzing your financial documents for insights &
              analytics
            </li>
            <li>Personalize your experience</li>
            <li>Improve security and maintain platform integrity</li>
            <li>Communicate with you (support, notifications, updates)</li>
            <li>Analyze usage patterns using Google Analytics and Microsoft Clarity</li>
            <li>Process payments securely via Stripe</li>
          </ul>
        </section>

        <!-- Section 4 -->
        <section>
          <h2 class="text-xl font-semibold mb-2">4. Cookies & Tracking Technology</h2>
          <p>
            Essential cookies support core functionality; others assist with analytics and
            personalization. You can disable non-essential cookies via your browser.
          </p>
          <p class="mt-2">Session storage: logout is manual or automatic once tokens expire.</p>
        </section>

        <!-- Section 5 -->
        <section>
          <h2 class="text-xl font-semibold mb-2">5. Data Storage & Security</h2>
          <ul class="list-disc pl-6 space-y-1">
            <li>Data stored on AWS (U.S.) with encryption at rest and in transit.</li>
            <li>Email access via secure tokens—credentials are not stored.</li>
            <li>Users must protect their own account credentials.</li>
          </ul>
          <p class="mt-2">
            <strong>Limitation of Liability:</strong> Despite sound security practices (encryption,
            authentication), no system is 100% secure. You acknowledge inherent risks such as
            cyberattacks or zero-day exploits. We are not liable for unauthorized access or data
            loss beyond our control.
          </p>
        </section>

        <!-- Section 6 -->
        <section>
          <h2 class="text-xl font-semibold mb-2">6. Sharing Your Information</h2>
          <p>We do not sell your data. We may share it with trusted third parties for:</p>
          <ul class="list-disc pl-6 space-y-1">
            <li>AI data processing via Gemini</li>
            <li>Secure transactions with Stripe</li>
            <li>Usage analytics with Google and Microsoft Clarity</li>
            <li>Legal compliance or law enforcement requests</li>
          </ul>
        </section>

        <!-- Section 7 -->
        <section>
          <h2 class="text-xl font-semibold mb-2">7. Your Data Rights</h2>
          <ul class="list-disc pl-6 space-y-1">
            <li>Access and download your data</li>
            <li>Correct or update your information</li>
            <li>Delete your account and data</li>
            <li>Withdraw consent for email monitoring or analytics</li>
            <li>Opt-out of analytics via email at info@billytics.pro</li>
          </ul>
        </section>

        <!-- Section 8 -->
        <section>
          <h2 class="text-xl font-semibold mb-2">8. Data Retention</h2>
          <p>
            We keep data only as long as needed to provide the Service or meet legal requirements.
            Deleted accounts are purged from our systems within a reasonable timeframe.
          </p>
        </section>

        <!-- Section 9 -->
        <section>
          <h2 class="text-xl font-semibold mb-2">9. Global Data Transfers</h2>
          <p>
            By using our Service, you consent to data being transferred, stored, and processed in
            countries like the U.S. and Canada. We maintain compliance with relevant data protection
            laws.
          </p>
        </section>

        <!-- Section 10 -->
        <section>
          <h2 class="text-xl font-semibold mb-2">10. Legal Bases for Processing (EU/UK Users)</h2>
          <ul class="list-disc pl-6 space-y-1">
            <li>
              <strong>Consent</strong> — obtained at sign-up (e.g., for analytics or email
              monitoring)
            </li>
            <li>
              <strong>Performance of a Contract</strong> — required for account, data fetching,
              analytics
            </li>
            <li>
              <strong>Legitimate Interests</strong> — app optimization, fraud prevention, etc.
            </li>
            <li><strong>Legal Obligations</strong> — compliance with applicable laws</li>
          </ul>
        </section>

        <!-- Section 11 -->
        <section>
          <h2 class="text-xl font-semibold mb-2">11. Data Breach Policy</h2>
          <p>
            In case of a data breach, affected users will be notified promptly via email and in-app
            notice. A response plan is activated immediately to mitigate and resolve the issue.
          </p>
        </section>

        <!-- Section 12 -->
        <section>
          <h2 class="text-xl font-semibold mb-2">12. Regulatory Compliance</h2>
          <ul class="list-disc pl-6 space-y-1">
            <li>GDPR (EU/UK)</li>
            <li>CCPA (California, USA)</li>
            <li>PIPEDA (Canada)</li>
            <li>Other region-specific privacy laws as applicable</li>
          </ul>
        </section>

        <!-- Section 13 -->
        <section>
          <h2 class="text-xl font-semibold mb-2">13. Third-Party Services</h2>
          <p>We integrate with trusted third-party services:</p>
          <ul class="list-disc pl-6 space-y-1">
            <li>Google Analytics (usage tracking)</li>
            <li>Stripe (payment processing)</li>
            <li>AWS (secure storage)</li>
            <li>Microsoft Clarity (website analytics)</li>
          </ul>
          <p class="mt-2">
            Email provider APIs (Google Workspace, Outlook, etc.) are used strictly to fetch billing
            data—not to train or improve AI models. All API usage adheres to the provider’s
            policies.
          </p>
        </section>

        <!-- Section 14 -->
        <section>
          <h2 class="text-xl font-semibold mb-2">14. Children’s Privacy</h2>
          <p>
            Not intended for individuals under 18. If we become aware of any personal data from
            minors, we will delete it immediately.
          </p>
        </section>

        <!-- Section 15 -->
        <section>
          <h2 class="text-xl font-semibold mb-2">15. Complaint Process</h2>
          <p>
            If you believe your data rights were violated, contact us at
            <a href="mailto:info@billytics.pro" class="text-blue-600">info@billytics.pro</a>.
            Complaints are investigated and resolved promptly.
          </p>
        </section>

        <!-- Section 16 -->
        <section>
          <h2 class="text-xl font-semibold mb-2">16. Updates to This Policy</h2>
          <p>
            We may update this Privacy Policy. Significant changes will be communicated by email or
            through the app. Continued use of the Service constitutes acceptance of those changes.
          </p>
        </section>

        <!-- Section 17 -->
        <section>
          <h2 class="text-xl font-semibold mb-2">17. Contact Information</h2>
          <p><strong>General:</strong> info@billytics.pro</p>
          <p><strong>Analytics Opt-Out:</strong> info@billytics.pro</p>
          <p class="mt-4 text-sm text-gray-600">© 2025 Billytics. All rights reserved.</p>
        </section>
      </mat-card-content>
    </mat-card>
  </div>
</app-billytics-public-container-component>
